/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import parsePhoneNumber from "libphonenumber-js";
import React, { useMemo } from "react";
import { useEffect, useRef, useState } from "react";
import { FaCopy, FaFileAlt, FaPencilAlt } from "react-icons/fa";
import { IoMdMail } from "react-icons/io";
import { TiArrowSortedDown } from "react-icons/ti";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { ContactApi } from "../../api/Contacts";
import ArrowDownIcon from "../../assets/icon/arrow-down";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import { LabelAtom } from "../../atoms/LabelAtom";
import { SelectedContactsAtom } from "../../atoms/SelectedContactsAtom";
import { UserAtom, UserCustomCopyTemplates } from "../../atoms/UserAtom";
import { ContactStatus } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import useMediaQuery from "../../hook/useMediaQuery";
import { customTooltip, notSelect } from "../../styles/general";
import { IContact } from "../../types/Contact";
import { ILabel } from "../../types/LabelTypes";
import { ICustomCopyTemplates } from "../../types/UserTypes";
import {
  IVirtualField,
  parseVirtualField,
} from "../../utils/ParseVirtualFields";
import AvatarIcon from "../Avatar";
import LabelSelect from "../Label/LabelSelects";
import SendEmailToContact from "../Modal/container/SendEmailToContact";
import { useModal } from "../Modal/Modal";
import StyledCheckBox from "../StyledCheckBox";
import CustomCopyTemplatesPopover from "./CustomCopyTemplatesPopover";
import ContactsDetails from "./DetailsScreens/ContactsDetails";
import EditEmailPopOver from "./EditEmailPopOver";
import MoreActionsPopOver from "./MoreActionsPopOver";
import StatusTypesModal from "./StatusTypesModal";

const TableRow = styled.div<TableProps>`
  display: grid;
  border: 1px solid;
  border-color: ${(props) => (props.check ? "#FB972E" : "#ffffff")};
  background-color: ${(props) =>
    props.check ? "rgb(251, 151, 46, 0.05)" : "#ffffff"};
  grid-template-rows: ${(props) =>
    props.show ? "60px 1fr" : "repeat(1, 1fr) "};
  align-items: center;
`;

const CheckboxTableTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;

  .e1axkcwe0 {
    min-width: 15px;
    min-height: 15px;
    max-width: 15px;
    max-height: 15px;

    margin: 0 24px 0 0 !important;
  }
`;

const GridRow = styled.div`
  width: 100%;
  height: 60px;

  background-color: #ffffff;

  display: grid;
  grid-template-columns:
    219px 64px 180px minmax(90px, 160px) 120px minmax(110px, 180px)
    60px;
  grid-column-gap: 24px;

  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1140px) {
    width: fit-content;
  }
`;

const StatusTagStyle = css`
  width: max-content;

  height: 28px;
  border-radius: 2px;
  padding: 10px 8px;
  font-size: 16px;
  font-weight: 600;

  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const TableTitle = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 16px;
  align-items: center;
  overflow: hidden;

  &.Confirmed {
    background: #eaf5e7;
    color: #75b85c;
    ${StatusTagStyle}
  }

  &.Pending {
    background: #fdf5e1;
    color: #e3a600;
    ${StatusTagStyle}
  }

  &.Not.Confirmed {
    background: #f2f2f2;
    color: #aaaaaa;
    ${StatusTagStyle}
  }

  &.Suppressed {
    background: #d6d6d6;
    color: #333333;
    ${StatusTagStyle}
  }

  &.Bot.Filled,
  &.Suspended {
    background: #f9e7e5;
    color: #c41700;
    ${StatusTagStyle}
  }

  &.Spam {
    background: #feede7;
    color: #e25723;
    ${StatusTagStyle}
  }

  &.Unsubscribed,
  &.Admin.Unsubscribed {
    background: #e8ecef;
    color: #688195;
    ${StatusTagStyle}
  }
`;

const Name = styled.span`
  /* max-width: 10vw; */
  margin-left: 5px;
`;

const TextCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const TableActionsRow = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: space-between;
`;

export const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const ActionIcon = styled.div<{ isShowing: boolean }>`
  display: flex;
  cursor: pointer;
  ${notSelect}
  transform: rotate(0deg);
  transition: 0.3s;

  ${(props) =>
    props.isShowing &&
    `    transform: rotate(180deg);
    `}
`;

const EditEmailContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  grid-gap: 5px;

  margin-left: 5px;

  .chakra-popover__popper {
    z-index: 9 !important;
  }

  .icon-button-edit-wrapper {
    width: fit-content;
    height: fit-content;

    border-radius: 4px;
    background-color: #fff2e5;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-button-edit {
    width: 32px;
    height: 32px;

    &.h-24 {
      width: 24px;

      background-color: #ffe7d4;
    }

    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;

    border-radius: 4px;
    background-color: #fff2e5;

    svg {
      max-width: 100%;
      width: 15px;
    }

    img {
      max-width: 100%;
      width: 14px;
    }
  }
`;

const marginRight15 = css`
  margin-right: 15px;
`;

const marginRight5 = css`
  margin-right: 5px;
`;

interface TableProps {
  show: boolean;
  check: boolean;
}

// interface IContactRow {
//   name: string;
//   email: string;
//   _id: string;
//   userUid: string;
//   labels: string[];
//   tags: IContactTag[];
//   avatar: string;
//   status: ContactStatus;
//   createdAt: Date;
//   phone?: string;
//   contact: IContact;
// }

export function getStatus(status: string) {
  if (status === ContactStatus.CONFIRMED) return "Confirmed";
  if (status === ContactStatus.NOT_CONFIRMED) return "Not Confirmed";
  if (status === ContactStatus.SPAM) return "Spam";
  if (status === ContactStatus.BOT_FILLED) return "Bot Filled";
  if (status === ContactStatus.SUSPENDED) return "Suspended";
  if (status === ContactStatus.SUPRESSED) return "Suppressed";
  if (status === ContactStatus.PENDING_CONFIRMATION) return "Pending";
  if (status === ContactStatus.ADMIN_UNSUBSCRIBED) return "Admin Unsubscribed";
  return "Unsubscribed";
}

interface Props {
  row: IContact;
  isShowing: boolean;
  handleShowCard: (id: string) => void;
  resetSearch: () => void;
  searchContacts?: (signal?: AbortSignal) => Promise<{
    contacts: IContact[];
    total: number;
  }>;
}

const STATUS_TO_SEND_EMAIL = [
  ContactStatus.CONFIRMED,
  ContactStatus.NOT_CONFIRMED,
];

export default function TableRows(props: Props) {
  const [
    isCustomCopyTemplatesPopoverOpen,
    setIsCustomCopyTemplatesPopoverOpen,
  ] = useState(false);

  function onOpenCustomCopyTemplatesPopoverOpen() {
    setIsCustomCopyTemplatesPopoverOpen(true);
  }

  function onCloseCustomCopyTemplatesPopoverOpen() {
    setIsCustomCopyTemplatesPopoverOpen(false);
  }

  const { row, isShowing, handleShowCard, resetSearch, searchContacts } = props;
  const isLargerThan1200 = useMediaQuery("(min-width: 1200px)");
  let setContacts = useSetRecoilState(ContactsAtom);
  const [showCard, setShowCard] = useState<string>("");
  const [selectedContacts, setSelectedContacts] =
    useRecoilState(SelectedContactsAtom);
  const labels = useRecoilValue(LabelAtom);
  const user = useRecoilValue(UserAtom);
  const [isHoveringEmail, setIsHoveringEmail] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const editContainerRef = useClickOutside(() => {
    setIsHoveringEmail(false);
    setIsEditingEmail(false);
  });

  const modal = useModal();
  const [phoneNumber, setPhoneNumber] = useState("-");

  const userCustomCopyTemplates = useRecoilValue(UserCustomCopyTemplates);
  const customCopyTemplatesFiltered = useMemo(() => {
    return userCustomCopyTemplates
      .map((t) => ({ ...t, template: t.template.trim() }))
      .filter((t) => !!t.template);
  }, [userCustomCopyTemplates]);

  useEffect(() => {
    if (row.phone) {
      const phone = row.phone.replace(/\D/g, "");

      const parsedPhone = parsePhoneNumber("+" + phone);

      if (!parsedPhone) {
        setPhoneNumber(row.phone);
        return;
      }

      const displayPhone = parsedPhone.formatNational();
      setPhoneNumber("+" + parsedPhone.countryCallingCode + " " + displayPhone);
    }
  }, [row]);

  const editButtonRef = useRef<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * useEffect that closes the DropView when a scroll is detected, much like
   * the native <select />
   */
  // useEffect(() => {
  //   function closeDropView() {
  //     setIsEditingEmail(false);
  //   }

  //   const scrollParent = getScrollParent(
  //     containerRef.current,
  //     containerRef.current
  //   );

  //   if (!scrollParent) return () => {};

  //   scrollParent.addEventListener("scroll", closeDropView);

  //   return () => {
  //     scrollParent.removeEventListener("scroll", closeDropView);
  //   };
  // }, [editContainerRef, isEditingEmail]);

  /**
   * On window resize, close dropview
   */
  useEffect(() => {
    window.addEventListener("resize", function close() {
      setIsEditingEmail(false);
    });
    return () => window.removeEventListener("resize", close);
  }, []);

  function getStatus(status: string) {
    if (status === ContactStatus.CONFIRMED) return "Confirmed";
    if (status === ContactStatus.NOT_CONFIRMED) return "Not Confirmed";
    if (status === ContactStatus.SPAM) return "Spam";
    if (status === ContactStatus.BOT_FILLED) return "Bot Filled";
    if (status === ContactStatus.SUSPENDED) return "Suspended";
    if (status === ContactStatus.SUPRESSED) return "Suppressed";
    if (status === ContactStatus.PENDING_CONFIRMATION) return "Pending";
    if (status === ContactStatus.ADMIN_UNSUBSCRIBED)
      return "Admin Unsubscribed";
    return "Unsubscribed";
  }

  function handleCheck(id: string) {
    setSelectedContacts((elems) => {
      if (elems.includes(id)) {
        return elems.filter((check) => check !== id);
      }
      return [...elems, id];
    });
  }

  async function handleUpdateContact(contact: IContact) {
    try {
      if (!contact || !user) return;

      await ContactApi.update(contact, contact._id);
    } catch (e) {
      console.log(e);
    }
  }

  function handleCustomCopy(customCopy: ICustomCopyTemplates) {
    const customCopyField: IVirtualField = {
      key: "", // noop by roger

      template: customCopy.template,

      // fields, were an old
      fields: customCopy.template
        .split("{{")
        .map((raw) => raw.split("}}")[0])
        .map(
          (customFieldName) =>
            customFieldName.toLowerCase().split("contact.")[1]
        )
        .filter((f) => !!f),
    };

    const x = parseVirtualField({
      contact: row as any,
      field: customCopyField,
    });

    const formattedX = x
      .split("\n")
      .map((i) => i.trim())
      .join("\n");

    navigator.clipboard.writeText(formattedX);

    if (formattedX.trim().length === 0) {
      toast("Your custom copy is empty", {
        type: "warning",
      });
    } else {
      toast("Copied to clipboard!", {
        type: "success",
      });
    }
  }

  async function selectLabelHandler(label: ILabel, contact: IContact) {
    if (contact) {
      const isLabelIn = contact.labels.includes(label._id);

      const newLabelsArray = isLabelIn
        ? // If label in, filters it out
          contact.labels.filter((l) => {
            return l !== label._id;
          })
        : // If label is not in, add it in
          [...contact.labels, label._id];

      const updatedContact = { ...contact, labels: newLabelsArray };
      await handleUpdateContact(updatedContact);
      setContacts((cs) =>
        cs
          ? cs?.map((c) => (c._id === updatedContact._id ? updatedContact : c))
          : []
      );
    }
  }

  return (
    <TableRow
      check={selectedContacts.includes(row._id)}
      show={!showCard.localeCompare(row._id)}
      key={row._id}
      css={css`
        margin-top: 7px;
      `}
    >
      <>
        <GridRow>
          <CheckboxTableTitleWrapper>
            <StyledCheckBox
              customCss={marginRight15}
              check={selectedContacts.includes(row._id)}
              handleCheck={() => handleCheck(row._id)}
            />
            <TableTitle>
              <div css={marginRight5}>
                <AvatarIcon
                  email={row.email}
                  image={row.avatar}
                  size={36}
                  userName={row.firstName?.toLocaleUpperCase() || ""}
                />
              </div>
              <Name
                css={css`
                  ${TextCss}
                  color: #333;
                  font-weight: 600;
                `}
              >
                {`${row.firstName || ""} ${row.lastName || ""}`}
              </Name>
            </TableTitle>
          </CheckboxTableTitleWrapper>

          <LabelSelect
            activeLabels={
              labels
                ? labels.filter((l) => {
                    if (row.labels?.includes(l._id as string)) return true;

                    return false;
                  })
                : []
            }
            selectLabelHandler={(l) => selectLabelHandler(l, row)}
            noFilter
          />

          <TableTitle
            css={css`
              color: #fb972e;
              font-weight: 600;
              cursor: pointer;
            `}
            ref={containerRef}
            onMouseEnter={() => setIsHoveringEmail(true)}
            onMouseLeave={() => {
              !isEditingEmail && setIsHoveringEmail(false);
            }}
          >
            <span css={TextCss}>{row.email}</span>
            {(isHoveringEmail || isCustomCopyTemplatesPopoverOpen) && (
              <EditEmailContainer ref={editContainerRef}>
                {/* Simple Copy */}
                {STATUS_TO_SEND_EMAIL.includes(row.status) && (
                  <>
                    <div
                      data-tip
                      data-for="send-email"
                      className="icon-button-edit"
                      onClick={() => {
                        modal.pushModal(
                          <SendEmailToContact contact={row} />,
                          `Send a one-off email to ${row.firstName} ${
                            row.lastName && row.lastName + " "
                          }<${row.email}>`
                        );
                      }}
                    >
                      <IoMdMail />
                    </div>
                    <ReactTooltip
                      id="send-email"
                      place="top"
                      effect="solid"
                      globalEventOff="click"
                      backgroundColor="#333333"
                      css={css`
                        margin-left: 0px !important;
                        ${customTooltip}
                      `}
                    >
                      <TooltipText>Send email</TooltipText>
                    </ReactTooltip>
                  </>
                )}

                <ReactTooltip
                  id="copy-contact"
                  place="top"
                  effect="solid"
                  globalEventOff="click"
                  backgroundColor="#333333"
                  css={css`
                    margin-left: 0px !important;
                    ${customTooltip}
                  `}
                >
                  <TooltipText>Copy</TooltipText>
                </ReactTooltip>

                {
                  // I have custom copy templates
                  customCopyTemplatesFiltered.length > 0 &&
                    // The default isn't blank
                    customCopyTemplatesFiltered[0].template !== "" && (
                      <div className="icon-button-edit-wrapper">
                        <div
                          data-tip
                          data-for="templates-default-contact"
                          className="icon-button-edit"
                          // ! Pedro-Mateus
                          onClick={() => {
                            // Try to find the "Default" if clicking on icon, else, [0]
                            const customCopy =
                              customCopyTemplatesFiltered.find(
                                (c) => c.title === "Default"
                              ) || customCopyTemplatesFiltered[0];

                            handleCustomCopy(customCopy);
                            // handleCustomCopyDefault
                          }}
                        >
                          <img
                            src="/icons/FaCopyFilled.svg"
                            alt="Custom Copy"
                          />
                        </div>

                        {/* I have more than one template, show the dropdown */}
                        {customCopyTemplatesFiltered.length > 1 && (
                          <CustomCopyTemplatesPopover
                            isOpen={isCustomCopyTemplatesPopoverOpen}
                            onClose={onCloseCustomCopyTemplatesPopoverOpen}
                            templateCopyArray={customCopyTemplatesFiltered}
                            onSelected={(selectedCustomCopy) => {
                              handleCustomCopy(selectedCustomCopy);
                            }}
                          >
                            <div
                              data-tip
                              data-for="templates-contact"
                              className="icon-button-edit h-24"
                              onClick={onOpenCustomCopyTemplatesPopoverOpen}
                            >
                              <TiArrowSortedDown />
                            </div>
                          </CustomCopyTemplatesPopover>
                        )}
                      </div>
                    )
                }

                <ReactTooltip
                  id="templates-default-contact"
                  place="top"
                  effect="solid"
                  globalEventOff="click"
                  backgroundColor="#333333"
                  css={css`
                    margin-left: 0px !important;
                    ${customTooltip}
                  `}
                >
                  <TooltipText>Custom Copy</TooltipText>
                </ReactTooltip>

                <ReactTooltip
                  id="templates-contact"
                  place="top"
                  effect="solid"
                  globalEventOff="click"
                  backgroundColor="#333333"
                  css={css`
                    margin-left: 0px !important;
                    ${customTooltip}
                  `}
                >
                  <TooltipText>Templates</TooltipText>
                </ReactTooltip>

                <ReactTooltip
                  id="edit-contact"
                  place="top"
                  effect="solid"
                  globalEventOff="click"
                  backgroundColor="#333333"
                  css={css`
                    margin-left: 0px !important;
                    ${customTooltip}
                  `}
                >
                  <TooltipText>Edit</TooltipText>
                </ReactTooltip>

                <div
                  data-tip
                  data-for="copy-contact"
                  className="icon-button-edit"
                  onClick={() => {
                    navigator.clipboard.writeText(row.email);

                    //That's not explicit in the design, but I thought it was necessary for the UX
                    toast("Email successfully copied to the clipboard!", {
                      type: "success",
                    });
                  }}
                >
                  <FaCopy />
                </div>
                <ReactTooltip
                  id="copy-contact"
                  place="top"
                  effect="solid"
                  globalEventOff="click"
                  backgroundColor="#333333"
                  css={css`
                    margin-left: 0px !important;
                    ${customTooltip}
                  `}
                >
                  <TooltipText>Copy</TooltipText>
                </ReactTooltip>

                <ReactTooltip
                  id="custom-copy"
                  place="top"
                  effect="solid"
                  globalEventOff="click"
                  backgroundColor="#333333"
                  css={css`
                    margin-left: 0px !important;
                    ${customTooltip}
                  `}
                >
                  <TooltipText>Custom Copy</TooltipText>
                </ReactTooltip>

                {/* Edit Email Adress and Send email confirmation checkbox */}
                {!isEditingEmail && (
                  <div
                    data-tip
                    data-for="edit-contact"
                    className="icon-button-edit"
                    onClick={() => {
                      setIsEditingEmail(!isEditingEmail);
                    }}
                    ref={editButtonRef}
                  >
                    <FaPencilAlt />
                  </div>
                )}

                {isEditingEmail && (
                  <EditEmailPopOver
                    onOpen={() => setIsEditingEmail(true)}
                    onClose={() => setIsEditingEmail(false)}
                    isOpen={isEditingEmail}
                    contactToEdit={row}
                  />
                )}
              </EditEmailContainer>
            )}
          </TableTitle>
          <TableTitle>{phoneNumber}</TableTitle>

          <TableTitle
            className={getStatus(row.status)}
            onClick={() => modal.pushModal(<StatusTypesModal />)}
          >
            {getStatus(row.status)}
          </TableTitle>
          <TableTitle>
            {isLargerThan1200
              ? dayjs(row.createdAt).format("MMM D, YYYY [@] h:mm a")
              : dayjs(row.createdAt).format("MM/DD/YYYY")}
          </TableTitle>
          <TableActionsRow>
            <Flex data-tip data-for={row._id + "see"}>
              <ReactTooltip
                id={row._id + "see"}
                place="top"
                effect="solid"
                globalEventOff="click"
                backgroundColor="#333333"
                css={customTooltip}
              >
                <TooltipText>See more</TooltipText>
              </ReactTooltip>

              {selectedContacts.length <= 1 && (
                <ActionIcon
                  isShowing={isShowing}
                  onClick={() => handleShowCard(row._id)}
                >
                  <ArrowDownIcon height={15} width={15} color="#868482" />
                </ActionIcon>
              )}
            </Flex>

            {selectedContacts.length <= 1 && (
              <MoreActionsPopOver
                resetSearch={resetSearch}
                contact={row}
                hasEmailConfirmation={user?.options?.sendEmailConfirmation}
              />
            )}
          </TableActionsRow>
        </GridRow>
        {isShowing && (
          <ContactsDetails searchContacts={searchContacts} contact={row} />
        )}
      </>
    </TableRow>
  );
}
